import { useCallback, useEffect, useState } from "react";

import {
  AgentModel,
  ChatStatus,
  FoundationModel,
  GetAgentConfigResponse,
  GetFoundationModelsResponse,
  SportSeason,
} from "@/components/agent-config/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

/**
 * Data access for the admin "Agent Configuration" page.
 *
 * Reads the active model + per-sport season context from
 * ``GET /admin/agent-config`` and the selectable model list from
 * ``GET /admin/agent-config/models``; persists the active model via
 * ``PUT /admin/agent-config/model`` and a sport's season via
 * ``PUT /admin/agent-config/sports/{sport}``; removes a sport via
 * ``DELETE /admin/agent-config/sports/{sport}``.
 *
 * Writes are optimistic and rolled back on failure, and they RETHROW so the
 * confirm / edit dialog that triggered them stays open for the user to retry.
 * The ``lastChangedBy``/``lastChangedAt`` attribution is adopted from the
 * server response (the client never knows its own admin identity ahead of the
 * write).
 */
const useAdminAgentConfig = () => {
  const [model, setModel] = useState<AgentModel | null>(null);
  const [sports, setSports] = useState<SportSeason[]>([]);
  const [chatStatus, setChatStatus] = useState<ChatStatus | null>(null);
  const [availableModels, setAvailableModels] = useState<FoundationModel[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  // isAdmin: true sends the access token the admin authorizer validates.
  const { apiGet, apiPut, apiDelete } = useApi({ isAdmin: true });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetAgentConfigResponse>("/admin/agent-config");
      if (response) {
        setModel(response.model ?? null);
        setSports(response.sports ?? []);
        setChatStatus(response.chatStatus ?? null);
      }
    } catch (error) {
      console.error(error);
      notifyError("Agent Config Error", "Failed to load agent configuration");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  const loadModels = useCallback(async () => {
    try {
      const response = await apiGet<GetFoundationModelsResponse>(
        "/admin/agent-config/models"
      );
      setAvailableModels(response?.models ?? []);
    } catch (error) {
      console.error(error);
      notifyError("Agent Config Error", "Failed to load available models");
    }
  }, [apiGet, notifyError]);

  const setActiveModel = useCallback(
    async (modelId: string, modelName: string) => {
      const previous = model;
      // Optimistic: reflect the new model immediately. Attribution is unknown
      // until the server responds, so carry the prior stamp for now.
      setModel((prev) => ({
        modelId,
        modelName,
        lastChangedBy: prev?.lastChangedBy ?? null,
        lastChangedAt: prev?.lastChangedAt ?? null,
      }));
      try {
        const response = await apiPut<{ model: AgentModel }>(
          "/admin/agent-config/model",
          { modelId, modelName }
        );
        if (response?.model) {
          setModel(response.model);
        }
      } catch (error) {
        console.error(error);
        setModel(previous);
        notifyError(
          "Agent Config Error",
          `Failed to set the active model to "${modelName}"`
        );
        throw error; // keep the confirm dialog open for retry
      }
    },
    [apiPut, notifyError, model]
  );

  const setChatEnabled = useCallback(
    async (enabled: boolean, reason?: string) => {
      const previous = chatStatus;
      // Optimistic: reflect the new switch state immediately. Attribution is
      // unknown until the server responds, so carry the prior stamp for now.
      setChatStatus((prev) => ({
        enabled,
        reason: reason?.trim() ? reason.trim() : null,
        lastChangedBy: prev?.lastChangedBy ?? null,
        lastChangedAt: prev?.lastChangedAt ?? null,
      }));
      try {
        const response = await apiPut<{ chatStatus: ChatStatus }>(
          "/admin/agent-config/chat-status",
          reason?.trim() ? { enabled, reason: reason.trim() } : { enabled }
        );
        if (response?.chatStatus) {
          setChatStatus(response.chatStatus);
        }
      } catch (error) {
        console.error(error);
        setChatStatus(previous);
        notifyError(
          "Agent Config Error",
          enabled ? "Failed to enable chat" : "Failed to disable chat"
        );
        throw error; // keep the confirm dialog open for retry
      }
    },
    [apiPut, notifyError, chatStatus]
  );

  const upsertSport = useCallback(
    async (sport: string, currentSeason: string, format: string) => {
      const previous = sports;
      const key = sport.trim().toUpperCase();
      // Optimistic upsert (add a new row, or update the matching one).
      setSports((prev) => {
        const exists = prev.some((s) => s.sport.toUpperCase() === key);
        if (exists) {
          return prev.map((s) =>
            s.sport.toUpperCase() === key ? { ...s, currentSeason, format } : s
          );
        }
        return [
          ...prev,
          {
            sport: sport.trim(),
            currentSeason,
            format,
            lastChangedBy: null,
            lastChangedAt: null,
          },
        ];
      });
      try {
        const response = await apiPut<{ sport: SportSeason }>(
          `/admin/agent-config/sports/${key}`,
          { currentSeason, format }
        );
        if (response?.sport) {
          const saved = response.sport;
          setSports((prev) =>
            prev.map((s) => (s.sport.toUpperCase() === key ? saved : s))
          );
        }
      } catch (error) {
        console.error(error);
        setSports(previous);
        notifyError("Agent Config Error", `Failed to save the ${sport} season`);
        throw error; // keep the edit/add dialog open for retry
      }
    },
    [apiPut, notifyError, sports]
  );

  const deleteSport = useCallback(
    async (sport: string) => {
      const previous = sports;
      const key = sport.trim().toUpperCase();
      // Optimistic: drop the row immediately, restore it if the call fails.
      setSports((prev) => prev.filter((s) => s.sport.toUpperCase() !== key));
      try {
        await apiDelete(`/admin/agent-config/sports/${key}`);
      } catch (error) {
        console.error(error);
        setSports(previous);
        notifyError("Agent Config Error", `Failed to delete the ${sport} season`);
        throw error; // keep the confirm dialog open for retry
      }
    },
    [apiDelete, notifyError, sports]
  );

  useEffect(() => {
    loadConfig();
    loadModels();
  }, [loadConfig, loadModels]);

  return {
    model,
    sports,
    chatStatus,
    availableModels,
    loading,
    loadConfig,
    setActiveModel,
    setChatEnabled,
    upsertSport,
    deleteSport,
  };
};

export default useAdminAgentConfig;
